import React from 'react';
import { Tooltip } from '@mui/material';
import { StepItem } from 'new/elements/Form/components';

type StepStatus = 'active' | 'done' | 'next';

interface StageComponent {
  nodeTitle: string;
  disabled?: boolean;
  disabledTooltip?: string;
}

interface FormHeaderStagesProps {
  activeStageIndex: number;
  stageComponents: StageComponent[];
  numStages?: number;
  isBackActionAllowed: boolean;
  onBackButtonClicked?: () => void;
  /** Jump directly to an earlier stage. When provided, ANY earlier stage is
   *  clickable (not just the adjacent one); otherwise legacy adjacent-back. */
  onStageClick?: (stageIndex: number) => void;
}

/**
 * A prior stage is navigable when back is allowed, it's earlier than the active
 * stage, and either we can jump to any stage (onStageClick provided) or it's the
 * immediately-previous stage (legacy onBackButtonClicked behavior).
 */
const shouldAllowStageBackAction = (
  isBackActionAllowed: boolean,
  activeStageIndex: number,
  stageIndex: number,
  canJumpToAnyStage: boolean,
): boolean => {
  return (
    isBackActionAllowed &&
    stageIndex >= 0 &&
    activeStageIndex > 0 &&
    stageIndex < activeStageIndex &&
    (canJumpToAnyStage || stageIndex === activeStageIndex - 1)
  );
};

export const FormHeaderStages: React.FC<FormHeaderStagesProps> = ({
  activeStageIndex,
  stageComponents,
  numStages = 1,
  isBackActionAllowed,
  onBackButtonClicked,
  onStageClick,
}) => {
  const canJumpToAnyStage = typeof onStageClick === 'function';
  const stageTitles = stageComponents.map((item: StageComponent, idx: number) => {
    const isDisabled = item?.disabled === true;
    const status: StepStatus = StepItem.getStatus(activeStageIndex, idx);
    const shouldRenderStepBackAction =
      shouldAllowStageBackAction(isBackActionAllowed, activeStageIndex, idx, canJumpToAnyStage) &&
      !isDisabled;

    const stepKey = `step_${item?.nodeTitle}_${idx}`;
    const cursor = isDisabled ? 'not-allowed' : shouldRenderStepBackAction ? 'pointer' : 'default';

    // Prefer a direct jump to this stage; fall back to the legacy single-step back.
    const navigateToStage = () => {
      if (!shouldRenderStepBackAction) return;
      if (onStageClick) {
        onStageClick(idx);
      } else {
        onBackButtonClicked?.();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (shouldRenderStepBackAction && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        navigateToStage();
      }
    };

    const stepContent = (
      <div
        key={stepKey}
        role="button"
        tabIndex={shouldRenderStepBackAction ? 0 : -1}
        aria-label={`Go back to ${item.nodeTitle}`}
        aria-disabled={!shouldRenderStepBackAction}
        style={{
          cursor,
          opacity: isDisabled ? 0.6 : undefined,
        }}
        onClick={navigateToStage}
        onKeyDown={handleKeyDown}
      >
        <StepItem index={idx} name={item.nodeTitle} status={status} />
      </div>
    );

    if (isDisabled && item?.disabledTooltip) {
      return (
        <Tooltip key={stepKey} title={item.disabledTooltip} placement="bottom">
          <span style={{ display: 'inline-block' }}>{stepContent}</span>
        </Tooltip>
      );
    }
    return stepContent;
  });

  const stageView = numStages > 1 ? <div className="nav-items">{stageTitles}</div> : null;

  return <div className="nav-items-container">{stageView}</div>;
};

export default FormHeaderStages;
